import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { tenantLifecycleApi } from '../services/tenantLifecycleApi';
import { useDebounce } from '../hooks/useDebounce';
import type { TenantDeletionPreviewResult, TenantLifecycleListItem, TenantTier } from '../types/tenantLifecycle';
import { formatDate } from '../utils/dateFormat';

const INITIAL_REASON = 'Approved tenant cleanup request';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const TIERS: TenantTier[] = ['Trial', 'Starter', 'Professional', 'Enterprise'];

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(textarea);
  return ok;
}

function CopyableValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="text-sm">
      <span className="mb-1 block font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 break-all rounded bg-gray-100 px-2 py-1 font-mono text-gray-900 dark:bg-gray-800 dark:text-gray-100">
          {value}
        </code>
        <button
          type="button"
          className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          aria-label={`Copy ${label.toLowerCase()}`}
          onClick={() => {
            void copyToClipboard(value).then((ok) => {
              setCopied(ok);
              if (ok) window.setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function getErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data as
    | { detail?: string; title?: string; errors?: Record<string, string[]> | string[] }
    | undefined;
  if (data) {
    if (data.errors) {
      const messages = Array.isArray(data.errors) ? data.errors : Object.values(data.errors).flat();
      if (messages.length > 0) return messages.join(' ');
    }
    if (data.detail) return data.detail;
    if (data.title) return data.title;
  }
  return (error as Error)?.message || fallback;
}

function isExpired(value?: string | null): boolean {
  return Boolean(value && new Date(value).getTime() < Date.now());
}

function toDateInput(value?: string | null): string {
  return value ? new Date(value).toISOString().slice(0, 10) : '';
}

function addDaysFromToday(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function TenantsListPage() {
  const confirmNameInputRef = useRef<HTMLInputElement>(null);
  const tierSelectRef = useRef<HTMLSelectElement>(null);
  const [search, setSearch] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const [deleteTenant, setDeleteTenant] = useState<TenantLifecycleListItem | null>(null);
  const [typedTenantName, setTypedTenantName] = useState('');
  const [reason, setReason] = useState(INITIAL_REASON);
  const [preview, setPreview] = useState<TenantDeletionPreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [subscriptionTenant, setSubscriptionTenant] = useState<TenantLifecycleListItem | null>(null);
  const [tier, setTier] = useState<TenantTier>('Trial');
  const [expiresOn, setExpiresOn] = useState('');
  const [subscriptionReason, setSubscriptionReason] = useState('');
  const [isSavingSubscription, setIsSavingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  const protectionReasonInputRef = useRef<HTMLInputElement>(null);
  const [protectionTenant, setProtectionTenant] = useState<TenantLifecycleListItem | null>(null);
  const [protectionReason, setProtectionReason] = useState('');
  const [isSavingProtection, setIsSavingProtection] = useState(false);
  const [protectionError, setProtectionError] = useState<string | null>(null);

  const approvalReasonInputRef = useRef<HTMLInputElement>(null);
  const [approvalTenant, setApprovalTenant] = useState<TenantLifecycleListItem | null>(null);
  const [approvalReason, setApprovalReason] = useState('');
  const [approvalAcknowledged, setApprovalAcknowledged] = useState(false);
  const [isSavingApproval, setIsSavingApproval] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 250);

  const tenantsQuery = useQuery({
    queryKey: ['tenant-lifecycle-tenants'],
    queryFn: () => tenantLifecycleApi.listTenants(),
    staleTime: 60_000,
  });

  const filtered = useMemo(
    () => (tenantsQuery.data ?? []).filter((tenant) => tenant.name.toLowerCase().includes(debouncedSearch.toLowerCase())),
    [debouncedSearch, tenantsQuery.data],
  );

  const openDeleteDialog = async (tenant: TenantLifecycleListItem) => {
    setDeleteTenant(tenant);
    setTypedTenantName('');
    setReason(INITIAL_REASON);
    setPreview(null);
    setPreviewError(null);
    setDeleteError(null);
    setStatusMessage(null);
    setIsPreviewLoading(true);
    try {
      setPreview(await tenantLifecycleApi.preview(tenant.id));
    } catch (e) {
      setPreviewError(getErrorMessage(e, 'Could not load the deletion impact.'));
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteTenant(null);
    setPreview(null);
    setPreviewError(null);
    setDeleteError(null);
  };

  const nameMatches = Boolean(deleteTenant && typedTenantName.trim() === deleteTenant.name);
  const canScheduleDeletion = Boolean(
    deleteTenant && preview?.canScheduleDeletion && nameMatches && reason.trim().length > 0,
  );

  const scheduleDeletion = async () => {
    if (!deleteTenant || !preview || !canScheduleDeletion) return;

    setIsScheduling(true);
    setDeleteError(null);
    try {
      const result = await tenantLifecycleApi.schedule({
        tenantId: deleteTenant.id,
        typedTenantName: typedTenantName.trim(),
        reason: reason.trim(),
        previewVersion: preview.previewVersion,
        idempotencyKey: crypto.randomUUID(),
        operationId: crypto.randomUUID(),
      });
      setStatusMessage(result.statusMessage);
      await tenantsQuery.refetch();
      closeDeleteDialog();
    } catch (e) {
      setDeleteError(getErrorMessage(e, 'Scheduling failed.'));
    } finally {
      setIsScheduling(false);
    }
  };

  const restoreTenant = async (tenant: TenantLifecycleListItem) => {
    try {
      const previewResponse = await tenantLifecycleApi.preview(tenant.id);
      await tenantLifecycleApi.restore({
        tenantId: tenant.id,
        previewVersion: previewResponse.previewVersion,
      });
      setStatusMessage(`Tenant ${tenant.name} restored.`);
      await tenantsQuery.refetch();
    } catch (e) {
      setStatusMessage(getErrorMessage(e, 'Restore failed.'));
    }
  };

  const openSubscriptionDialog = (tenant: TenantLifecycleListItem) => {
    const currentTier = (TIERS as string[]).includes(tenant.tier) ? (tenant.tier as TenantTier) : 'Trial';
    setSubscriptionTenant(tenant);
    setTier(currentTier);
    setExpiresOn(
      currentTier === 'Trial' && (isExpired(tenant.expiresAtUtc) || !tenant.expiresAtUtc)
        ? addDaysFromToday(30)
        : toDateInput(tenant.expiresAtUtc),
    );
    setSubscriptionReason('');
    setSubscriptionError(null);
    setStatusMessage(null);
  };

  const closeSubscriptionDialog = () => {
    setSubscriptionTenant(null);
    setSubscriptionError(null);
  };

  const canSaveSubscription = Boolean(
    subscriptionTenant && subscriptionReason.trim().length > 0 && (tier !== 'Trial' || expiresOn),
  );

  const saveSubscription = async () => {
    if (!subscriptionTenant || !canSaveSubscription) return;

    setIsSavingSubscription(true);
    setSubscriptionError(null);
    try {
      const result = await tenantLifecycleApi.updateSubscription({
        tenantId: subscriptionTenant.id,
        tier,
        expiresAtUtc: expiresOn ? `${expiresOn}T23:59:59Z` : null,
        reason: subscriptionReason.trim(),
      });
      setStatusMessage(
        `${subscriptionTenant.name} is now ${result.tier}${result.expiresAtUtc ? ` until ${formatDate(result.expiresAtUtc)}` : ' with no expiry'}.`,
      );
      await tenantsQuery.refetch();
      closeSubscriptionDialog();
    } catch (e) {
      setSubscriptionError(getErrorMessage(e, 'Could not update the subscription.'));
    } finally {
      setIsSavingSubscription(false);
    }
  };

  const openProtectionDialog = (tenant: TenantLifecycleListItem) => {
    setProtectionTenant(tenant);
    setProtectionReason('');
    setProtectionError(null);
    setStatusMessage(null);
  };

  const closeProtectionDialog = () => {
    setProtectionTenant(null);
    setProtectionError(null);
  };

  const saveProtection = async () => {
    if (!protectionTenant || protectionReason.trim().length === 0) return;

    const enable = !protectionTenant.isProtected;
    setIsSavingProtection(true);
    setProtectionError(null);
    try {
      await tenantLifecycleApi.setDeletionProtection({
        tenantId: protectionTenant.id,
        enabled: enable,
        reason: protectionReason.trim(),
      });
      setStatusMessage(
        enable
          ? `${protectionTenant.name} is now protected from deletion.`
          : `Deletion protection removed from ${protectionTenant.name}.`,
      );
      await tenantsQuery.refetch();
      closeProtectionDialog();
    } catch (e) {
      setProtectionError(getErrorMessage(e, 'Could not change deletion protection.'));
    } finally {
      setIsSavingProtection(false);
    }
  };

  const openApprovalDialog = (tenant: TenantLifecycleListItem) => {
    setApprovalTenant(tenant);
    setApprovalReason('');
    setApprovalAcknowledged(false);
    setApprovalError(null);
    setStatusMessage(null);
  };

  const closeApprovalDialog = () => {
    setApprovalTenant(null);
    setApprovalError(null);
  };

  // Revoking only needs a reason. Approving also needs the reviewed deletion operation and an explicit acknowledgement.
  const canSaveApproval = Boolean(
    approvalTenant &&
      approvalReason.trim().length > 0 &&
      (approvalTenant.purgeApproved || (approvalAcknowledged && approvalTenant.operationId)),
  );

  const saveApproval = async () => {
    if (!approvalTenant || !canSaveApproval) return;

    const approve = !approvalTenant.purgeApproved;
    setIsSavingApproval(true);
    setApprovalError(null);
    try {
      const result = await tenantLifecycleApi.setPurgeApproval({
        tenantId: approvalTenant.id,
        approved: approve,
        operationId: approvalTenant.operationId ?? EMPTY_GUID,
        reason: approvalReason.trim(),
      });
      setStatusMessage(`${approvalTenant.name}: ${result.statusMessage}`);
      await tenantsQuery.refetch();
      closeApprovalDialog();
    } catch (e) {
      setApprovalError(getErrorMessage(e, 'Could not change purge approval.'));
      void tenantsQuery.refetch();
    } finally {
      setIsSavingApproval(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold">Tenants</h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        Manage tenant subscriptions, deletion protection, deletion and purge progress.
      </p>
      <input
        className="input mt-4 max-w-md"
        placeholder="Search by tenant name"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        aria-label="Search tenants"
      />
      {tenantsQuery.isError ? (
        <p className="mt-3 text-sm text-amber-600">
          Could not load tenant lifecycle list. Verify GET /api/system/tenant-lifecycle/tenants.
        </p>
      ) : null}
      {statusMessage ? (
        <p className="mt-3 text-sm text-primary-300" role="status">
          {statusMessage}
        </p>
      ) : null}

      <div className="mt-4 overflow-x-auto card">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="p-2">Name</th>
              <th className="p-2">State</th>
              <th className="p-2">Tier</th>
              <th className="p-2">Expires</th>
              <th className="p-2">Protected</th>
              <th className="p-2">Recovery Deadline</th>
              <th className="p-2">Purge Approval</th>
              <th className="p-2">Purge Progress</th>
              <th className="p-2">Created Date</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((tenant) => {
              const expired = isExpired(tenant.expiresAtUtc);
              return (
                <tr key={tenant.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="p-2">
                    <Link className="text-primary-400 hover:text-primary-300 hover:underline" to={`/tenants/${tenant.id}`}>
                      {tenant.name}
                    </Link>
                  </td>
                  <td className="p-2">{tenant.lifecycleState}</td>
                  <td className="p-2">{tenant.tier ?? '—'}</td>
                  <td className="p-2">
                    {tenant.expiresAtUtc ? (
                      <span className={expired ? 'text-amber-400' : undefined}>
                        {formatDate(tenant.expiresAtUtc)}
                        {expired ? ' (expired)' : ''}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="p-2">{tenant.isProtected ? 'Yes' : 'No'}</td>
                  <td className="p-2">{tenant.purgeAfterUtc ? formatDate(tenant.purgeAfterUtc) : '—'}</td>
                  <td className="p-2">
                    {tenant.purgeApproved ? (
                      <span className="text-amber-400">Approved</span>
                    ) : tenant.lifecycleState === 'PendingDeletion' ? (
                      'Not approved'
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="p-2">
                    SQL {tenant.sqlDeletedRows} / Cosmos {tenant.cosmosDeletedItems} / Blob {tenant.blobDeletedItems}
                  </td>
                  <td className="p-2">{tenant.createdAt ? formatDate(tenant.createdAt) : '—'}</td>
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="rounded bg-primary-700 px-3 py-1 text-xs text-white disabled:opacity-60"
                        onClick={() => openSubscriptionDialog(tenant)}
                        disabled={tenant.lifecycleState !== 'Active'}
                      >
                        Subscription
                      </button>
                      <button
                        type="button"
                        className="rounded bg-gray-700 px-3 py-1 text-xs text-white disabled:opacity-60"
                        onClick={() => openProtectionDialog(tenant)}
                        disabled={tenant.lifecycleState === 'Purging' || tenant.lifecycleState === 'Purged'}
                      >
                        {tenant.isProtected ? 'Unprotect' : 'Protect'}
                      </button>
                      <button
                        type="button"
                        className="rounded bg-red-700 px-3 py-1 text-xs text-white disabled:opacity-60"
                        onClick={() => void openDeleteDialog(tenant)}
                        disabled={tenant.isProtected || tenant.lifecycleState === 'Purging' || tenant.lifecycleState === 'Purged'}
                        title={tenant.isProtected ? 'Protected from deletion. Select Unprotect first.' : undefined}
                      >
                        Schedule Delete
                      </button>
                      {tenant.lifecycleState === 'PendingDeletion' ? (
                        <button
                          type="button"
                          className="rounded bg-gray-700 px-3 py-1 text-xs text-white"
                          onClick={() => void restoreTenant(tenant)}
                        >
                          Restore
                        </button>
                      ) : null}
                      {tenant.lifecycleState === 'PendingDeletion' ? (
                        <button
                          type="button"
                          className={`rounded px-3 py-1 text-xs text-white disabled:opacity-60 ${tenant.purgeApproved ? 'bg-gray-700' : 'bg-red-700'}`}
                          onClick={() => openApprovalDialog(tenant)}
                          disabled={!tenant.purgeApproved && (tenant.isProtected || !tenant.operationId)}
                          title={
                            tenant.purgeApproved
                              ? undefined
                              : tenant.isProtected
                                ? 'Protected from deletion. Select Unprotect first.'
                                : !tenant.operationId
                                  ? 'This deletion request has no operation ID. Restore the tenant and schedule deletion again.'
                                  : undefined
                          }
                        >
                          {tenant.purgeApproved ? 'Revoke approval' : 'Approve purge'}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 ? (
              <tr>
                <td className="p-3 text-gray-500" colSpan={10}>
                  No tenants found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={deleteTenant !== null}
        onClose={closeDeleteDialog}
        onConfirm={() => {
          void scheduleDeletion();
        }}
        title={deleteTenant ? `Schedule deletion for ${deleteTenant.name}` : 'Schedule deletion'}
        message="The tenant is disabled immediately. You can restore it for 7 days. After that, its data can be permanently purged once a platform administrator approves the purge."
        confirmLabel={isScheduling ? 'Scheduling...' : 'Schedule deletion'}
        variant="danger"
        loading={isScheduling}
        confirmDisabled={!canScheduleDeletion || isScheduling}
        initialFocusRef={confirmNameInputRef}
      >
        {deleteTenant ? (
          <div className="space-y-3 rounded border border-gray-200 bg-gray-50 p-4 text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200">
            <CopyableValue label="Tenant name" value={deleteTenant.name} />
            <CopyableValue label="Tenant ID" value={deleteTenant.id} />

            <div className="text-sm">
              {isPreviewLoading ? <p className="text-gray-500 dark:text-gray-400">Loading deletion impact...</p> : null}
              {previewError ? <p className="text-amber-700 dark:text-amber-300">{previewError}</p> : null}
              {preview ? (
                <>
                  <p>
                    Impact: {preview.impactSummary.activeUsers} active users, {preview.impactSummary.activeApiKeys} API keys,{' '}
                    {preview.impactSummary.activeIngestionEndpoints} ingestion endpoints.
                  </p>
                  <p>Recovery deadline: {preview.recoveryDeadlineUtc ? formatDate(preview.recoveryDeadlineUtc) : '—'}</p>
                  {preview.blockingReasons.length > 0 ? (
                    <ul className="mt-2 list-disc pl-6 text-amber-700 dark:text-amber-300">
                      {preview.blockingReasons.map((reasonItem) => (
                        <li key={reasonItem}>{reasonItem}</li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : null}
            </div>

            <div className="text-sm">
              <label className="mb-1 block font-medium text-gray-700 dark:text-gray-300" htmlFor="typed-tenant-name-input">
                Type the tenant name to confirm
              </label>
              <input
                id="typed-tenant-name-input"
                ref={confirmNameInputRef}
                className="input w-full"
                placeholder={deleteTenant.name}
                autoComplete="off"
                value={typedTenantName}
                aria-invalid={typedTenantName.length > 0 && !nameMatches}
                onChange={(e) => setTypedTenantName(e.target.value)}
              />
              {typedTenantName.length > 0 && !nameMatches ? (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">The name must match exactly (case-sensitive).</p>
              ) : null}
            </div>
            <div className="text-sm">
              <label className="mb-1 block font-medium text-gray-700 dark:text-gray-300" htmlFor="tenant-deletion-reason-input">
                Reason
              </label>
              <input
                id="tenant-deletion-reason-input"
                className="input w-full"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            {deleteError ? <p className="text-sm text-red-600 dark:text-red-400">{deleteError}</p> : null}
          </div>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={subscriptionTenant !== null}
        onClose={closeSubscriptionDialog}
        onConfirm={() => {
          void saveSubscription();
        }}
        title={subscriptionTenant ? `Subscription for ${subscriptionTenant.name}` : 'Subscription'}
        message="Change the tier or extend the trial. AI is unavailable for Trial tenants after the expiry date."
        confirmLabel={isSavingSubscription ? 'Saving...' : 'Save subscription'}
        loading={isSavingSubscription}
        confirmDisabled={!canSaveSubscription || isSavingSubscription}
        initialFocusRef={tierSelectRef}
      >
        {subscriptionTenant ? (
          <div className="space-y-3 rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200">
            <p className="text-gray-700 dark:text-gray-300">
              Current: {subscriptionTenant.tier}
              {subscriptionTenant.expiresAtUtc
                ? `, expires ${formatDate(subscriptionTenant.expiresAtUtc)}${isExpired(subscriptionTenant.expiresAtUtc) ? ' (expired)' : ''}`
                : ', no expiry'}
            </p>
            <div>
              <label className="mb-1 block font-medium text-gray-700 dark:text-gray-300" htmlFor="tenant-tier-select">
                Tier
              </label>
              <select
                id="tenant-tier-select"
                ref={tierSelectRef}
                className="input w-full"
                value={tier}
                onChange={(e) => {
                  const nextTier = e.target.value as TenantTier;
                  setTier(nextTier);
                  if (nextTier === 'Trial' && !expiresOn) setExpiresOn(addDaysFromToday(30));
                }}
              >
                {TIERS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block font-medium text-gray-700 dark:text-gray-300" htmlFor="tenant-expires-input">
                {tier === 'Trial' ? 'Trial ends on (UTC)' : 'Subscription ends on (UTC, optional)'}
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id="tenant-expires-input"
                  type="date"
                  className="input"
                  min={addDaysFromToday(1)}
                  value={expiresOn}
                  onChange={(e) => setExpiresOn(e.target.value)}
                />
                <button type="button" className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700" onClick={() => setExpiresOn(addDaysFromToday(30))}>
                  +30 days
                </button>
                <button type="button" className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700" onClick={() => setExpiresOn(addDaysFromToday(90))}>
                  +90 days
                </button>
                {tier !== 'Trial' ? (
                  <button type="button" className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700" onClick={() => setExpiresOn('')}>
                    No expiry
                  </button>
                ) : null}
              </div>
            </div>
            <div>
              <label className="mb-1 block font-medium text-gray-700 dark:text-gray-300" htmlFor="tenant-subscription-reason-input">
                Reason (audited)
              </label>
              <input
                id="tenant-subscription-reason-input"
                className="input w-full"
                value={subscriptionReason}
                onChange={(e) => setSubscriptionReason(e.target.value)}
              />
            </div>
            {subscriptionError ? <p className="text-red-600 dark:text-red-400">{subscriptionError}</p> : null}
          </div>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={protectionTenant !== null}
        onClose={closeProtectionDialog}
        onConfirm={() => {
          void saveProtection();
        }}
        title={
          protectionTenant
            ? `${protectionTenant.isProtected ? 'Remove deletion protection from' : 'Protect'} ${protectionTenant.name}`
            : 'Deletion protection'
        }
        message={
          protectionTenant?.isProtected
            ? 'After you remove protection, platform administrators can schedule this tenant for deletion.'
            : protectionTenant?.lifecycleState === 'PendingDeletion'
              ? 'A protected tenant cannot be purged until protection is removed. Any purge approval is cancelled and must be given again later.'
              : 'A protected tenant cannot be scheduled for deletion or purged until protection is removed.'
        }
        confirmLabel={
          isSavingProtection ? 'Saving...' : protectionTenant?.isProtected ? 'Remove protection' : 'Protect tenant'
        }
        variant={protectionTenant?.isProtected ? 'danger' : undefined}
        loading={isSavingProtection}
        confirmDisabled={protectionReason.trim().length === 0 || isSavingProtection}
        initialFocusRef={protectionReasonInputRef}
      >
        {protectionTenant ? (
          <div className="space-y-3 rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200">
            <div>
              <label className="mb-1 block font-medium text-gray-700 dark:text-gray-300" htmlFor="tenant-protection-reason-input">
                Reason
              </label>
              <input
                id="tenant-protection-reason-input"
                ref={protectionReasonInputRef}
                className="input w-full"
                placeholder="Why are you changing deletion protection?"
                value={protectionReason}
                onChange={(e) => setProtectionReason(e.target.value)}
              />
            </div>
            {protectionError ? <p className="text-red-600 dark:text-red-400">{protectionError}</p> : null}
          </div>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={approvalTenant !== null}
        onClose={closeApprovalDialog}
        onConfirm={() => {
          void saveApproval();
        }}
        title={
          approvalTenant
            ? `${approvalTenant.purgeApproved ? 'Revoke purge approval for' : 'Approve purge for'} ${approvalTenant.name}`
            : 'Purge approval'
        }
        message={
          approvalTenant?.purgeApproved
            ? 'Revoking blocks the purge until a platform administrator approves it again. It only works before the purge starts.'
            : 'Approving lets the platform permanently delete all data for this tenant once its recovery period ends. This cannot be undone.'
        }
        confirmLabel={isSavingApproval ? 'Saving...' : approvalTenant?.purgeApproved ? 'Revoke approval' : 'Approve purge'}
        variant={approvalTenant?.purgeApproved ? undefined : 'danger'}
        loading={isSavingApproval}
        confirmDisabled={!canSaveApproval || isSavingApproval}
        initialFocusRef={approvalReasonInputRef}
      >
        {approvalTenant ? (
          <div className="space-y-3 rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200">
            <CopyableValue label="Tenant name" value={approvalTenant.name} />
            <CopyableValue label="Tenant ID" value={approvalTenant.id} />
            <p>Recovery deadline: {approvalTenant.purgeAfterUtc ? formatDate(approvalTenant.purgeAfterUtc) : '—'}</p>
            {!approvalTenant.purgeApproved ? (
              <p className="text-amber-700 dark:text-amber-300">
                {isExpired(approvalTenant.purgeAfterUtc)
                  ? 'The recovery deadline has already passed. If physical purge is enabled for this environment, the purge can start within minutes.'
                  : 'The purge starts after the recovery deadline, if physical purge is enabled for this environment. Until then you can still revoke this approval or restore the tenant.'}
              </p>
            ) : null}
            <div>
              <label className="mb-1 block font-medium text-gray-700 dark:text-gray-300" htmlFor="tenant-purge-approval-reason-input">
                Reason (audited)
              </label>
              <input
                id="tenant-purge-approval-reason-input"
                ref={approvalReasonInputRef}
                className="input w-full"
                placeholder="Why are you changing purge approval?"
                value={approvalReason}
                onChange={(e) => setApprovalReason(e.target.value)}
              />
            </div>
            {!approvalTenant.purgeApproved ? (
              <label className="flex items-start gap-2" htmlFor="tenant-purge-approval-ack">
                <input
                  id="tenant-purge-approval-ack"
                  type="checkbox"
                  className="mt-1"
                  checked={approvalAcknowledged}
                  onChange={(e) => setApprovalAcknowledged(e.target.checked)}
                />
                <span>I have reviewed this tenant and understand its data will be permanently deleted.</span>
              </label>
            ) : null}
            {approvalError ? <p className="text-red-600 dark:text-red-400">{approvalError}</p> : null}
          </div>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
